import React, { useState } from 'react';
import { MapDialog } from '../../../easymap/mapDialog/MapDialog';
import { AffActivityCalendar } from './AffActivityCalendar';
import { AFF_AIRCRAFT_COLUMNS } from '../../../shared/affSchema';
import { metresToFeet, metresPerSecondToKnots } from '../../../shared/affUnits';
import { dateTimeInNwt, nwtLocalDateTimeToIso } from '../../../utils/nwtDate';
import { AffAircraftEntity } from './affServiceAdapter';
import { AffAsOf } from './AffAsOf';
import './AffAircraftModals.scss';

interface AffAircraftDetailModalProps {
  aircraft: AffAircraftEntity;
  onClose: () => void;
  /** Either a fixed window (hours) or an explicit range (start/end) — never both (#801). */
  onShowTracks: (esn: string, span: { hours?: number; start?: string; end?: string }) => void;
  /** When this view of the aircraft was taken (epoch ms). See AffAircraftModalHost. */
  asOf?: number;
}

/** Durations the map action offers. Must match AFF_TRACK_HOURS_OPTIONS on the router. */
export const AFF_TRACK_HOURS_OPTIONS = [24, 48, 72] as const;

/** The fourth choice: an explicit start and end rather than a window from now (#801). */
export const AFF_TRACK_CUSTOM = 'custom' as const;

/** 48h is what the live AFF map layer has always drawn — keep it the default. */
export const AFF_TRACK_MAP_HOURS = 48;

const display = (v: unknown): string =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/**
 * Units (#810). Speed is stored in metres per second and altitude in metres
 * above sea level; the Aviation group asked to read knots and feet.
 *
 * The converted value leads and the stored value follows in brackets, so the
 * panel answers the question asked without making the underlying record
 * unverifiable — that record is what the exports and the database carry.
 *
 * The conversion is derived from the raw field rather than the API's virtual
 * columns because an aircraft opened from a map marker is built from the live
 * layer's GeoJSON properties, which do not carry them.
 */
const CONVERTED: Record<string, { raw: string; unit: string; convert: (v: number | null | undefined) => number | null }> = {
  altFeet: { raw: 'alt', unit: 'm', convert: metresToFeet },
  speedKnots: { raw: 'speed', unit: 'm/s', convert: metresPerSecondToKnots },
};

/**
 * #798 — timestamp fields render as NWT local date and time. They arrive as ISO
 * strings from the grid API and as epoch milliseconds from the live map layer;
 * dateTimeInNwt takes either.
 */
const TIMESTAMP_FIELDS = new Set(['positionTimestamp', 'createdAt']);

/**
 * #798 — the Aviation group listed these as noise on the panel. `stale` stays in
 * the schema and the grid, where it dims the row and feeds the summary's worded
 * notice; it is simply not a field a reader needs spelled out here.
 */
const OMITTED_FROM_PANEL = new Set(['stale']);

/** The raw columns are shown inside their converted row, not as rows of their own. */
const RAW_FIELDS = new Set(Object.values(CONVERTED).map((c) => c.raw));

const displayConverted = (aircraft: AffAircraftEntity, field: string): string => {
  const spec = CONVERTED[field];
  const rawValue = (aircraft as any)[spec.raw] as number | null | undefined;
  const converted = spec.convert(rawValue);
  if (converted === null) return '—';
  return `${converted} (${Math.round(rawValue as number)} ${spec.unit})`;
};

/**
 * Second level of the drill-down: every field the schema carries, plus the
 * option to put this aircraft's recent track on the map.
 *
 * The field list is derived from AFF_AIRCRAFT_COLUMNS rather than hand-written,
 * so a column added to the schema shows up here too instead of quietly going
 * missing from the detail view.
 */
const AffAircraftDetailModal: React.FC<AffAircraftDetailModalProps> = ({
  aircraft,
  onClose,
  onShowTracks,
  asOf,
}) => {
  const [hours, setHours] = useState<number>(AFF_TRACK_MAP_HOURS);
  const [isCustom, setIsCustom] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  // A range is only drawable once both ends are set and in order. Refusing here
  // is what stops the router having to guess what a half-specified range meant.
  // Both ends are NWT wall-clock time (#866 U6), so they are compared as NWT.
  const startIso = nwtLocalDateTimeToIso(start);
  const endIso = nwtLocalDateTimeToIso(end);
  const rangeIsUsable = !!startIso && !!endIso && startIso <= endIso;
  const canDraw = !!aircraft.esn && (!isCustom || rangeIsUsable);

  return (
  <MapDialog
    isOpen
    moveable
    onClose={onClose}
    width={640}
    ariaLabel={`Aircraft ${display(aircraft.name)} details`}
    header={<span className="aff-modal-title">{display(aircraft.name)} — Details</span>}
  >
    <div className="aff-detail">
      <dl className="aff-field-grid">
        {AFF_AIRCRAFT_COLUMNS.filter((col) => !RAW_FIELDS.has(col.field) && !OMITTED_FROM_PANEL.has(col.field)).map((col) => (
          <React.Fragment key={col.field}>
            <dt>{col.title}</dt>
            <dd>
              {col.field in CONVERTED
                ? displayConverted(aircraft, col.field)
                : TIMESTAMP_FIELDS.has(col.field)
                  ? display(dateTimeInNwt((aircraft as any)[col.field]))
                  : display((aircraft as any)[col.field])}
            </dd>
          </React.Fragment>
        ))}
      </dl>

      <AffAsOf asOf={asOf} />

      {/* Which days this aircraft actually flew (#803) — read-only. */}
      <fieldset className="aff-activity-section">
        <legend>Activity</legend>
        <AffActivityCalendar esn={String(aircraft.esn)} />
      </fieldset>

      <fieldset className="aff-track-duration">
        <legend>Track duration</legend>
        {AFF_TRACK_HOURS_OPTIONS.map((option) => (
          <label key={option} className={!isCustom && hours === option ? 'is-selected' : ''}>
            <input
              type="radio"
              name="aff-track-hours"
              value={option}
              checked={!isCustom && hours === option}
              onChange={() => { setIsCustom(false); setHours(option); }}
            />
            {`${option}h`}
          </label>
        ))}
        <label className={isCustom ? 'is-selected' : ''}>
          <input
            type="radio"
            name="aff-track-hours"
            value={AFF_TRACK_CUSTOM}
            checked={isCustom}
            onChange={() => setIsCustom(true)}
          />
          Custom
        </label>

        {isCustom && (
          <div className="aff-track-range">
            <label htmlFor="aff-track-start">Start (NWT)</label>
            <input
              id="aff-track-start"
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
            <label htmlFor="aff-track-end">End (NWT)</label>
            <input
              id="aff-track-end"
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
        )}
      </fieldset>

      <div className="aff-modal-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() =>
            onShowTracks(String(aircraft.esn), isCustom ? { start, end } : { hours })
          }
          disabled={!canDraw}
        >
          Show Track on Map
        </button>
      </div>
    </div>
  </MapDialog>
  );
};

export default AffAircraftDetailModal;
